import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext.js';
import { RequireAuth } from './components/RequireAuth.js';
import { CallbackPage } from './pages/CallbackPage.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { GigEditorPage } from './pages/GigEditorPage.js';
import { PerformanceConsolePage } from './pages/PerformanceConsolePage.js';
import { RepertoirePage } from './pages/RepertoirePage.js';
import { IntegrationsPage } from './pages/IntegrationsPage.js';
import { ImprovProfilePage } from './pages/ImprovProfilePage.js';
import { RhythmCatalogPage } from './pages/RhythmCatalogPage.js';
import { SetlistEditorPage } from './pages/SetlistEditorPage.js';
import { TippingPage } from './pages/TippingPage.js';
import { GigAnalyticsPage } from './pages/GigAnalyticsPage.js';
import { ArtistAnalyticsPage } from './pages/ArtistAnalyticsPage.js';
import { OperationsPage } from './pages/OperationsPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { registerServiceWorker } from './lib/registerSw.js';
import './styles.css';

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');

// Authenticated admin surface, all routes under /app/* (§30.2).
createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/app/callback" element={<CallbackPage />} />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <OverviewPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/gigs/:gigId"
            element={
              <RequireAuth>
                <GigEditorPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/artists/:artistId/songs"
            element={
              <RequireAuth>
                <RepertoirePage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/artists/:artistId/integrations"
            element={
              <RequireAuth>
                <IntegrationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/artists/:artistId/improv-profiles"
            element={
              <RequireAuth>
                <ImprovProfilePage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/artists/:artistId/rhythm-catalogs"
            element={
              <RequireAuth>
                <RhythmCatalogPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/gigs/:gigId/setlist"
            element={
              <RequireAuth>
                <SetlistEditorPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/gigs/:gigId/tipping"
            element={
              <RequireAuth>
                <TippingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/gigs/:gigId/analytics"
            element={
              <RequireAuth>
                <GigAnalyticsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/artists/:artistId/analytics"
            element={
              <RequireAuth>
                <ArtistAnalyticsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/gigs/:gigId/operations"
            element={
              <RequireAuth>
                <OperationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/app/gigs/:gigId/console"
            element={
              <RequireAuth>
                <PerformanceConsolePage />
              </RequireAuth>
            }
          />
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);

// Offline console shell (§24.5); no-op where service workers are unavailable.
registerServiceWorker();
